'use client';

import classes from '@/components/providerInput/ProviderInput.module.scss';
import popupStyles from '@/components/swdAutocomplete/swdAutocompletePopup.module.scss';
import { TextField } from '@/components/textField';
import { suggestStreets } from '@/helper/streetCatalog';
import { useStreetService } from '@/hooks/useStreetService';
import ClearIcon from '@/public/icons/schliessen.svg';
import { Autocomplete } from '@base-ui/react/autocomplete';
import match from 'autosuggest-highlight/match';
import parse from 'autosuggest-highlight/parse';
import classNames from 'classnames';
import { type InputHTMLAttributes, type ReactNode, useDeferredValue, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

const CLEAR_REASONS = new Set(['clear-press', 'input-clear']);

type StreetHtmlInputProps = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'defaultValue' | 'disabled' | 'name' | 'onBlur' | 'onChange' | 'readOnly' | 'required' | 'value'
> & { 'data-testid'?: string };

export type StreetInputProps = {
  postcode: string;
  city: string;
  name: string;
  value: string;
  onValueChange: (value: string) => void;
  onStreetChange: (street: string | null) => void;
  onBlur?: () => void;
  id?: string;
  className?: string;
  label?: ReactNode;
  error?: boolean;
  helperText?: ReactNode;
  required?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  placeholder?: string;
  inputProps?: StreetHtmlInputProps;
  endAdornment?: ReactNode;
  size?: 'small' | 'medium';
};

export default function StreetInput({
  postcode,
  city,
  name,
  value,
  onValueChange,
  onStreetChange,
  onBlur,
  id,
  className,
  label,
  error,
  helperText,
  required,
  disabled,
  readOnly,
  placeholder,
  inputProps,
  endAdornment,
  size,
}: StreetInputProps) {
  const { t } = useTranslation();
  const popupAnchorRef = useRef<HTMLDivElement>(null);
  const deferredValue = useDeferredValue(value);
  const { catalog, loading, error: loadError, retry } = useStreetService(postcode, city);
  const options = useMemo(() => suggestStreets(catalog, deferredValue), [catalog, deferredValue]);
  const isDisabled = !postcode || !city || disabled;
  const hasQuery = Boolean(value.trim());
  const items = hasQuery ? options : [];

  return (
    <div className={classNames(classes.providerInput, className)}>
      <Autocomplete.Root
        autoHighlight
        disabled={isDisabled}
        readOnly={readOnly}
        filter={() => true}
        id={id}
        items={items}
        mode="list"
        openOnInputClick
        value={value}
        onOpenChange={(open) => {
          if (open && loadError) retry();
        }}
        onValueChange={(nextValue, eventDetails) => {
          if (eventDetails.reason === 'input-change') {
            onValueChange(nextValue);
            return;
          }

          if (CLEAR_REASONS.has(eventDetails.reason)) {
            onValueChange('');
            onStreetChange(null);
            return;
          }

          if (eventDetails.reason === 'item-press') {
            onStreetChange(nextValue);
          }
        }}
      >
        <Autocomplete.InputGroup className={popupStyles.inputGroup}>
          <Autocomplete.Input
            render={(baseInputProps) => (
              <TextField
                fullWidth
                required={required}
                name={name}
                id={id}
                inputAreaRef={popupAnchorRef}
                value={value}
                label={label}
                onBlur={onBlur}
                error={error}
                helperText={helperText}
                size={size}
                autoComplete="on"
                disabled={isDisabled}
                placeholder={placeholder}
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  ...(baseInputProps as React.InputHTMLAttributes<HTMLInputElement>),
                  ...inputProps,
                  'data-testid': inputProps?.['data-testid'] ?? 'street-input',
                }}
                InputProps={{
                  readOnly,
                  endAdornment: (
                    <>
                      <Autocomplete.Clear aria-label={t('Eingabefeld leeren')} className={popupStyles.clear}>
                        <ClearIcon aria-hidden="true" />
                      </Autocomplete.Clear>
                      {endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </Autocomplete.InputGroup>

        <Autocomplete.Portal>
          <Autocomplete.Positioner
            align="start"
            anchor={popupAnchorRef}
            className={popupStyles.positioner}
            side="bottom"
            sideOffset={8}
          >
            <Autocomplete.Popup className={popupStyles.popup} aria-busy={loading || undefined}>
              {/* <Autocomplete.Status className={popupStyles.status}>
              {loadError
                ? t('Straßen konnten nicht geladen werden.')
                : loading && options.length > 0
                  ? t('Laden…')
                  : null}
            </Autocomplete.Status> */}
              <Autocomplete.Empty className={hasQuery && options.length === 0 ? popupStyles.empty : undefined}>
                {hasQuery && !loadError ? (loading ? t('Laden…') : t('Keine Straßen gefunden.')) : null}
              </Autocomplete.Empty>
              <Autocomplete.List className={popupStyles.list}>
                {(option: string) => {
                  const matches = match(option, value, { insideWords: true });
                  const parts = parse(option, matches);

                  return (
                    <Autocomplete.Item key={option} className={popupStyles.option} nativeButton={false} value={option}>
                      <div className={popupStyles.optionContent}>
                        {parts.map((part, index) => (
                          <span
                            key={index}
                            data-testid={'street-input-item' + Number(index + 1)}
                            className={part.highlight ? popupStyles.highlightStrong : popupStyles.highlight}
                          >
                            {part.text}
                          </span>
                        ))}
                      </div>
                    </Autocomplete.Item>
                  );
                }}
              </Autocomplete.List>
            </Autocomplete.Popup>
          </Autocomplete.Positioner>
        </Autocomplete.Portal>
      </Autocomplete.Root>
    </div>
  );
}
